import { Button, Drawer, Empty, Input, Popconfirm, Spin, message as antdMessage } from "antd";
import { CheckOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { ContactAvatar } from "./ContactAvatar";
import type { GroupMember } from "../api/groups";
import { addMembers, removeMember, updateMemberVars } from "../api/groups";
import { listContacts } from "../api/contacts";
import type { Contact } from "../api/contacts";
import { useIsMobile } from "../hooks/useBreakpoint";

type Props = {
  groupId: string;
  members: GroupMember[];
  onChange: () => void;
};

function getVar(m: GroupMember, key: string): string {
  const v = m.vars?.[key];
  return typeof v === "string" ? v : "";
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: 14,
  color: "var(--ant-color-text-secondary)",
};
const td: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
};

export function MembersPanel({ groupId, members, onChange }: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const isMobile = useIsMobile();

  const onSaveVar = async (m: GroupMember, key: string, value: string) => {
    const current = (m.vars as Record<string, string>) ?? {};
    const next = { ...current, [key]: value };
    try {
      await updateMemberVars(groupId, m.contactId, next);
      onChange();
    } catch {
      antdMessage.error("保存失败");
    }
  };

  const onRemove = async (m: GroupMember) => {
    try {
      await removeMember(groupId, m.contactId);
      onChange();
      antdMessage.success("已移除");
    } catch {
      antdMessage.error("移除失败");
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 15, color: "var(--ant-color-text-secondary)" }}>
          {members.length} 个成员 · 编辑 Name / Company 用于群发模板变量
        </div>
        <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
          添加成员
        </Button>
      </div>

      {members.length === 0 ? (
        <Empty description="还没有成员" />
      ) : isMobile ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {members.map((m) => (
            <li
              key={m.contactId}
              style={{
                background: "var(--ant-color-bg-container)",
                border: "1px solid var(--ant-color-border)",
                borderRadius: 12,
                padding: 12,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ContactAvatar email={m.email} name={m.name} tier={m.tier} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{m.name || m.email}</div>
                  {m.name ? (
                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--ant-color-text-secondary)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.email}
                    </div>
                  ) : null}
                </div>
                <Popconfirm title="移除该成员?" onConfirm={() => onRemove(m)}>
                  <Button size="small" type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
              <Input
                size="small"
                addonBefore="Name"
                defaultValue={getVar(m, "Name") || m.name || ""}
                onBlur={(e) => onSaveVar(m, "Name", e.target.value)}
              />
              <Input
                size="small"
                addonBefore="Company"
                defaultValue={getVar(m, "Company")}
                onBlur={(e) => onSaveVar(m, "Company", e.target.value)}
                placeholder="可选"
              />
            </li>
          ))}
        </ul>
      ) : (
        <div
          style={{
            background: "var(--ant-color-bg-container)",
            border: "1px solid var(--ant-color-border)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
            <thead>
              <tr style={{ background: "var(--ant-color-bg-layout)" }}>
                <th style={th}>联系人</th>
                <th style={th}>邮箱</th>
                <th style={th}>{"{{Name}}"}</th>
                <th style={th}>{"{{Company}}"}</th>
                <th style={{ ...th, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.contactId} style={{ borderTop: "1px solid var(--ant-color-border)" }}>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <ContactAvatar email={m.email} name={m.name} tier={m.tier} size={28} />
                      <span>{m.name || "—"}</span>
                    </div>
                  </td>
                  <td style={{ ...td, color: "var(--ant-color-text-secondary)" }}>{m.email}</td>
                  <td style={td}>
                    <Input
                      size="small"
                      defaultValue={getVar(m, "Name") || m.name || ""}
                      onBlur={(e) => onSaveVar(m, "Name", e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={td}>
                    <Input
                      size="small"
                      defaultValue={getVar(m, "Company")}
                      onBlur={(e) => onSaveVar(m, "Company", e.target.value)}
                      style={{ width: "100%" }}
                      placeholder="可选"
                    />
                  </td>
                  <td style={td}>
                    <Popconfirm title="移除该成员?" onConfirm={() => onRemove(m)}>
                      <Button size="small" type="text" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddMembersDrawer
        groupId={groupId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existingIds={new Set(members.map((m) => m.contactId))}
        onAdded={() => {
          setAddOpen(false);
          onChange();
        }}
      />
    </>
  );
}

function AddMembersDrawer({
  groupId,
  open,
  onClose,
  existingIds,
  onAdded,
}: {
  groupId: string;
  open: boolean;
  onClose: () => void;
  existingIds: Set<string>;
  onAdded: () => void;
}) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQ("");
    setLoading(true);
    listContacts("all")
      .then(({ contacts }) => setContacts(contacts.filter((c) => !existingIds.has(c.id))))
      .finally(() => setLoading(false));
  }, [open, existingIds]);

  const filtered = q
    ? contacts.filter((c) => {
        const lc = q.toLowerCase();
        return c.email.toLowerCase().includes(lc) || c.name?.toLowerCase().includes(lc);
      })
    : contacts;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await addMembers(groupId, [...selected]);
      antdMessage.success(`已添加 ${selected.size} 人`);
      onAdded();
    } catch {
      antdMessage.error("添加失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title="添加成员"
      open={open}
      onClose={onClose}
      width={460}
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, color: "var(--ant-color-text-secondary)" }}>
            已选 {selected.size} 人
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose}>取消</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={submitting}
              disabled={selected.size === 0}
              onClick={onSubmit}
            >
              添加
            </Button>
          </div>
        </div>
      }
    >
      <Input
        placeholder="搜索联系人"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        prefix={<SearchOutlined style={{ color: "var(--ant-color-text-secondary)" }} />}
        allowClear
        style={{ marginBottom: 12 }}
      />
      {loading ? (
        <div style={{ display: "grid", placeItems: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description="无可添加联系人" />
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
          {filtered.map((c) => {
            const checked = selected.has(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => toggle(c.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 12px",
                    border: "1px solid",
                    borderColor: checked ? "var(--ant-color-primary)" : "var(--ant-color-border)",
                    background: checked
                      ? "color-mix(in srgb, var(--ant-color-primary) 8%, transparent)"
                      : "var(--ant-color-bg-container)",
                    color: "inherit",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <ContactAvatar email={c.email} name={c.name} tier={c.tier} size={32} interactive={false} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name || c.email}</div>
                    {c.name ? (
                      <div
                        style={{
                          fontSize: 14,
                          color: "var(--ant-color-text-secondary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.email}
                      </div>
                    ) : null}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}
